import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Point } from 'geojson';

import { SnLocation } from '@lib/api/entities';

/**
 * Utility class for location geometry calculations.
 * All methods are pure functions with no side effects.
 */
export class LocationGeometryUtils {
  /**
   * Calculates a bounding box string for a location with a buffer.
   * Uses the location's geofence if available, otherwise uses point geometry.
   *
   * @param location - The location to calculate bbox for
   * @param bufferInMeters - Buffer distance in meters (default: 100)
   * @returns Comma-separated bbox string: "minLng,minLat,maxLng,maxLat"
   *
   * @example
   * ```typescript
   * const bbox = LocationGeometryUtils.calculateVenueBbox(location, 100);
   * // Returns: "-122.42,37.774,-122.419,37.775"
   * ```
   */
  static calculateVenueBbox(location: SnLocation, bufferInMeters: number = 100): string {
    const bufferInKm = bufferInMeters / 1000;

    let geometry: Feature<MultiPolygon | Point>;

    if (location.geofence?.coordinates && location.geofence.coordinates.length > 0) {
      geometry = turf.multiPolygon(location.geofence.coordinates);
    } else {
      geometry = turf.point(location.geometry.coordinates);
    }

    const buffered = turf.buffer(geometry, bufferInKm, { units: 'kilometers' });
    const bbox = turf.bbox(buffered);

    return `${bbox[0]},${bbox[1]},${bbox[2]},${bbox[3]}`;
  }
}

